import React, { useRef, useState, useEffect } from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import { useTranslation } from 'react-i18next';

import 'filepond/dist/filepond.min.css';
import 'src/styles/Attachments.css';
import { AttachmentsDefaultProps, AttachmentsPropTypes } from 'src/components/Attachments/propTypes';
import {
  CONVERT_ATTACHMENT_TO_PDF,
  GET_ATTACHMENT_ID,
  GET_OBJECT_PRE_SIGNED_URL,
  PUT_OBJECT_PRE_SIGNED_URL,
} from 'src/graphql/attachment/query';
import AddButtonPlugin from 'src/components/Attachments/addButtonPlugin';
import client from 'src/utils/api/client';
import s from 'src/components/Attachments/Attachments.styles';
import moment from 'moment';

registerPlugin(AddButtonPlugin);

const LOADING_FILENAME = 'loading.file';

const Attachments = props => {
  const {
    allowMultiple = true,
    attachmentIds,
    canNotDownload = false,
    disabled = false,
    entity,
    labelFileProcessingComplete,
    labelTapToUndo,
    onChange: onChangeReal,
    onDeleteFile,
    style = 'default',
    doRerender = false,
  } = props;

  const uploadsInProgress = useRef(0);
  const cachedComponent = useRef();
  const  [disabledChanged, setDisabledChanged] = useState(false);

  const onChange = (...params) => {
    uploadsInProgress.current -= 1;
    return onChangeReal(...params, uploadsInProgress.current);
  };

  const { t } = useTranslation();
  const ref = useRef(null);
  const [params, setParams] = useState({
    files: attachmentIds.map(attachment => {
      return {
        attachmentId: attachment,
        options: {
          metadata: {
            attachmentId: attachment,
          },
          type: 'local',
        },
        source: LOADING_FILENAME,
      };
    }),
  });

  const getPreSignedUrl = async (id, realName) => {
    try {
      const response = await client.query({
        query: GET_OBJECT_PRE_SIGNED_URL,
        variables: realName
          ? {
            id,
            realName,
          }
          : { id },
      });
      return response.data.getObjectPreSignedUrl.url;
    } catch (e) {
      return null;
    }
  };

  const getPreSignedObj = async (id, realName) => {
    try {
      const response = await client.query({
        query: GET_OBJECT_PRE_SIGNED_URL,
        variables: realName
          ? {
            id,
            realName,
          }
          : { id },
      });
      return response.data.getObjectPreSignedUrl;
    } catch (e) {
      return null;
    }
  };

  const putPreSignedUrl = (callBack, fileName, fileSize) => {
    client
      .query({
        query: PUT_OBJECT_PRE_SIGNED_URL,
        variables: {
          entity,
          fileName,
          fileSize,
        },
      })
      .then(response => {
        // eslint-disable-next-line prefer-destructuring
        const url = response.data.putObjectPreSignedUrl.url;
        callBack(url);
      });
  };

  const getAttachmentId = (callBack, fileName, file, load) => {
    client
      .mutate({
        mutation: GET_ATTACHMENT_ID,
        variables: {
          entity,
          fileName,
        },
      })
      .then(response => {
        const { id } = response.data.persistAttachment;
        load(true);
        callBack(fileName, entity, id, {
          attId: id,
          name: file.name,
          size: file.size,
          type: file.type,
        });
      });
  };

  const convertPdf = async (fileName, originalAttId) => {
    const response = await client.mutate({
      mutation: CONVERT_ATTACHMENT_TO_PDF,
      variables: {
        id: originalAttId
      },
    });

    const { id, size } = response.data.convertAttachmentToPDF;

    const pdfFileName = `${fileName.substr(0, fileName.lastIndexOf('.'))}.pdf`;

    onChange(pdfFileName, 'pdf', id, {
      name: pdfFileName,
      type: 'pdf',
      size,
      attId: id,
    });
  };

  const getTimeStamp = (str) => {
    const date = new Date(str);
    const timestampInSeconds = Math.floor(date.getTime() / 1000);
    return timestampInSeconds;
  }

  useEffect(() => {
    async function fetchData() {
      const result = await Promise.all(
        attachmentIds.map(async attachmentId => {
          const obj = await getPreSignedObj(
            typeof attachmentId === 'object'
              ? attachmentId.attId
              : attachmentId,
          );
          return { attachmentId, url: obj?.url, uploadedAt: obj?.uploadedAt };
        }),
      );
      const resultOrderByUploadedAt = result.filter(res => res.url).sort((a, b) => {return getTimeStamp(b.uploadedAt) - getTimeStamp(a.uploadedAt)})
      setParams({
        files: resultOrderByUploadedAt.map(obj => {
          if (typeof obj.attachmentId === 'object') {
            const names = result.map(r=>r.attachmentId.name);

            const fileName = obj.attachmentId.name;
            const fileType = fileName.split('.').pop();
            const targeFileTypes = ['doc', 'docx', 'xls', 'xlsx'];
            const isTargetFileType = targeFileTypes.includes(fileType);

            const showPdfButton = isTargetFileType && !names.includes(`${fileName.substr(0, fileName.lastIndexOf('.'))}.pdf`);

            return {
              attachmentId: obj.attachmentId.attId,
              source: obj.url,
              options: {
                type: onDeleteFile ? 'limbo' : 'local',
                // mock file information
                file: {
                  attachmentId: obj.attachmentId.attId,
                  name: obj.url === null ? LOADING_FILENAME : obj.attachmentId.name,
                  showPdfButton,
                  size: obj.attachmentId.size,
                  type: obj.attachmentId.type,
                  uploadedAt: obj.uploadedAt,
                },
                metadata: {
                  attachmentId: obj.attachmentId.attId,
                },
              },
            };
          }
          return {
            attachmentId: obj.attachmentId,
            source: obj.url,
            options: {
              metadata: {
                attachmentId: obj.attachmentId,
              },
              type: onDeleteFile ? 'limbo' : 'local',
            },
          };
        }),
      });
    }
    if (attachmentIds.length > 0) {
      fetchData();
    } else {
      setParams({
        files: [],
      });
    }
  }, [attachmentIds.length]);
  useEffect(() => {
      setDisabledChanged(true);      
  }, [disabled]); 
  useEffect(() => {
    if(disabledChanged) setDisabledChanged(false);      
}, [disabledChanged]);

  const uploadFile = (fileName, url, file, progress, load, error, request) => {
    request.open('PUT', url);

    request.upload.onprogress = e => {
      progress(e.lengthComputable, e.loaded, e.total);
    };
    // eslint-disable-next-line func-names
    request.onload = function() {
      if (request.status >= 200 && request.status < 300) {
        getAttachmentId(onChange, fileName, file, load);
      } else {
        error('file upload failed');
      }
    };

    request.send(file);
  };

  // eslint-disable-next-line func-names
  const processFunction = function(
    fieldName,
    file,
    metadata,
    load,
    error,
    progress,
    abort,
  ) {
    const request = new XMLHttpRequest();
    const fileName = file.name + String(Date.now());
    const callBack = signedUrl =>
      uploadFile(fileName, signedUrl, file, progress, load, error, request);

    putPreSignedUrl(callBack, fileName, file.size);

    return {
      abort: () => {
        request.abort();
        abort();
      },
    };
  };

  const onactivatefile = async file => {
    if (canNotDownload) {
      return;
    }

    const result = params.files.find(x => x.source === file.source);
    let url;

    if (result) {
      const { options, attachmentId } = result;
      url = options?.file
        ? await getPreSignedUrl(attachmentId, options.file.name)
        : await getPreSignedUrl(attachmentId);
    }

    const oldNode = document.querySelector('#g-exportOrder-iframe');
    if (oldNode) {
      document.body.removeChild(
        document.querySelector('#g-exportOrder-iframe'),
      );
    }
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.id = 'g-exportOrder-iframe';
    iframe.src = url;
    document.body.appendChild(iframe);
  };

  const currentClassName = () => {
    let name = `${style}-attachment-component`;

    if (style === 'cap' && disabled) {
      name = name.concat(' cap-gray-attachment-component-hide-label');
    }

    return name;
  };

  const addUploadedAtToFileInfo = (file) => {
    const fileId = `li#filepond--item-${file?.id}`;
    const uploadedOn = file?.file.uploadedAt ? moment.utc(new Date(file?.file.uploadedAt)).format('DD-MMM-YYYY HH:mm:ss UTC') : '';
    const spanElement = document.createElement('span');
    spanElement.classList.add('UploadedAt');
    const text = document.createTextNode(uploadedOn);
    spanElement.appendChild(text);
    const filePonditem = document.querySelector(fileId);
    // eslint-disable-next-line
    filePonditem?.querySelector('div.filepond--file-info').appendChild(spanElement);
    
    if (filePonditem && !filePonditem?.querySelector('span.filepond--file-status-main')?.innerHTML) {
      filePonditem.querySelector('div.filepond--file-status').style.width = '2.25em';
    }
    
}

  const currentLabelIdle = () => {
    if (style === 'cap') {
      return t('DRAG_DROP');
    }
    return t('DRAG_DROP_WITH_LINK');
  };

  const renderComponent = () => (
    <s.Wrapper
      className={currentClassName()}
      isHovering={style === 'cap' && !disabled}
      >
      <FilePond
        {...params}
        allowBrowse={!disabled}
        allowDrop={!disabled}
        allowMultiple={allowMultiple}
        allowProcess={!disabled}
        allowRevert={!disabled}
        labelFileProcessingComplete={labelFileProcessingComplete}
        labelIdle={!disabled ? currentLabelIdle() : ''}
        labelTapToUndo={labelTapToUndo}
        maxParallelUploads={9999}
        onactivatefile={onactivatefile}
        onClickConvertPdf={convertPdf}
        onaddfilestart={file => {
          if (file?.filename !== LOADING_FILENAME) {
            uploadsInProgress.current += 1;
          }
        }}
        onaddfile={(error, file) => {
          if(style === 'cap'){
            addUploadedAtToFileInfo(file);
          }
        }}
        onremovefile={(error, file) => {
          if (onDeleteFile && file?.filename !== LOADING_FILENAME) {
            onDeleteFile(file.filename, file.getMetadata('attachmentId'));
          }
        }}
        ref={ref}
        server={{
          process: (fieldName, file, metadata, load, error, progress, abort) =>
            processFunction(
              fieldName,
              file,
              metadata,
              load,
              error,
              progress,
              abort,
            ),
        }}
      />
    </s.Wrapper>
  );

  if (uploadsInProgress.current === 0 || !!disabledChanged || !!doRerender) {
    cachedComponent.current = renderComponent();
  }

  return cachedComponent.current;
};

Attachments.propTypes = AttachmentsPropTypes;
Attachments.defaultProps = AttachmentsDefaultProps;

export default React.memo(Attachments);
